import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { CloseListingButton } from "@/components/CloseListingButton";
import { timeAgo, BLOCK_REASONS, listingPath } from "@/lib/constants";
import {
  Plus, ArrowUpToLine, Pencil, Ban, ShieldOff, ArrowRight, ExternalLink, CreditCard,
  AlertTriangle, CheckCircle2, Loader2, ShieldCheck, MessageSquare, Search, Store,
  Package, LayoutDashboard, ChevronRight, Cake, FileCheck, UserPlus, Copy, Check, Banknote,
  Trash2, FileText,
} from "lucide-react";
import { toast } from "@/lib/toast";
import FoundingClaimPrompt from "@/components/founding/FoundingClaimPrompt";
import MemberWarningsBanner from "@/components/MemberWarningsBanner";

const REASON_LABEL = Object.fromEntries(BLOCK_REASONS.map((r) => [r.id, r.label]));
const CEIL = "#DAD6CE"; // brightness ceiling — never pure white on dark surfaces
const money = (cents) =>
  "$" + ((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Tier / role chip — the tier's own ribbon color so the plan means something on sight.
function primaryChip(s, user) {
  if (s.is_admin) {
    const sup = user.role === "super_admin";
    return { label: sup ? "SUPER ADMIN" : "ADMIN", style: sup ? { background: "#E8C46A", color: "#1a1400" } : { background: "#B4462F", color: "#F0EBE2" } };
  }
  if (s.is_ambassador) return { label: "AMBASSADOR", style: { background: "#C2571F", color: "#F5EDE6" } };
  if (s.is_dealer) return { label: "DEALER", style: { background: "#1F7A4D", color: "#EAF3EC" } };
  const map = {
    standard: { label: "STANDARD", style: { background: "#57682A", color: CEIL } },
    preferred: { label: "PREFERRED", style: { background: "#CFE86B", color: "#141A05" } },
    elite: { label: "PRO", style: { background: "#E8C46A", color: "#1a1400" } },
  };
  return map[s.tier] || map.standard;
}

function Chip({ label, style, className = "", testid }) {
  return (
    <span data-testid={testid}
      className={`inline-flex items-center font-mono text-[10px] uppercase tracking-[0.1em] px-2 py-1 ${className}`}
      style={style}>{label}</span>
  );
}

// ---- Payouts (Stripe Connect) — a compact right-rail status block that routes to the working
// /settings/payouts page (the single onboarding + management surface). State is read LIVE from
// Stripe, never a stored boolean. Shown for EVERY account so the money path is never hidden.
function PayoutsRailCard() {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/dealer/stripe/status").then(({ data }) => setStatus(data)).catch(() => {}); }, []);

  let state = null;
  if (status) {
    if (status.reconnect_required) state = "reconnect";
    else if (status.payout_enabled || status.payout_chip === "enabled") state = "active";
    else if (status.connected) state = "pending";
    else if (status.payout_ready_live === "UNKNOWN") state = "unknown";
    else state = "not_started";
  }
  const MAP = {
    not_started: { label: "Not started", color: "#76797D", border: "#3E3F42", cta: "Set up payouts", copy: "Connect Stripe to receive money from your sales." },
    pending: { label: "Pending", color: "#E8C46A", border: "#E8C46A80", cta: "Finish setup", copy: "Stripe needs a few more details before payouts turn on." },
    active: { label: "Active", color: "#4e9e6f", border: "#1F7A4D", cta: "Manage payouts", copy: "Connected and payout-ready — released funds land here." },
    reconnect: { label: "Reconnect required", color: "#E0A08F", border: "#A33D2880", cta: "Reconnect payouts", copy: "Your payout account can't be reached — reconnect to release waiting sales." },
    unknown: { label: "Unknown", color: "#E8C46A", border: "#E8C46A80", cta: "Check payouts", copy: "We couldn't verify your payout status just now." },
  };
  const m = state ? MAP[state] : null;
  const awaiting = status?.sales_awaiting_payout || 0;

  return (
    <Card title="Payouts" testid="card-payouts" right={<Banknote className="w-4 h-4 text-[#76797D]" />}>
      <div className="p-4">
        {!m ? (
          <p className="font-mono text-[11px] uppercase text-[#76797D]" data-testid="payouts-rail-loading">Loading…</p>
        ) : (
          <>
            <span data-testid="payouts-rail-chip" className="inline-flex font-mono text-[10px] uppercase tracking-wide px-2 py-1 border" style={{ borderColor: m.border, color: m.color }}>{m.label}</span>
            <p className="text-[12.5px] font-body text-[#8B8D90] mt-2.5" data-testid="payouts-rail-copy">{m.copy}</p>
            {awaiting > 0 && state !== "active" && (
              <p className="text-[11px] font-mono uppercase text-[#E8C46A] mt-2 flex items-center gap-1.5" data-testid="payouts-rail-awaiting">
                <AlertTriangle className="w-3.5 h-3.5" /> {awaiting} sale{awaiting !== 1 ? "s" : ""} waiting on setup
              </p>
            )}
            <Link to="/settings/payouts" data-testid="payouts-rail-link"
              className="mt-3 inline-flex items-center gap-2 border border-[#2E2F31] hover:border-[#3E3F42] text-[#DAD6CE] font-mono uppercase text-[11px] tracking-wide px-4 py-2.5 transition-colors">
              <Banknote className="w-3.5 h-3.5" /> {m.cta} <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </>
        )}
      </div>
    </Card>
  );
}

// ---- ACTION TILE ---------------------------------------------------------
function ActionTile({ to, onClick, label, sub, filled, dimmed, testid }) {
  const base = "flex flex-col justify-between p-4 min-h-[74px] transition-colors text-left";
  const skin = filled
    ? "bg-olive hover:bg-olive-bright"
    : dimmed
    ? "bg-[#111213] border border-[#2E2F31] opacity-55 cursor-not-allowed"
    : "bg-[#111213] border border-[#2E2F31] hover:border-[#3E3F42]";
  const labelCls = `font-mono text-[10.5px] uppercase tracking-[0.1em] ${filled ? "text-white" : "text-[#DAD6CE]"}`;
  const subCls = `text-[11.5px] mt-2 ${filled ? "text-white/75" : "text-[#76797D]"}`;
  const inner = (<><span className={labelCls}>{label}</span><span className={subCls}>{sub}</span></>);
  if (dimmed) return <div className={`${base} ${skin}`} data-testid={testid}>{inner}</div>;
  if (to) return <Link to={to} className={`${base} ${skin}`} data-testid={testid}>{inner}</Link>;
  return <button onClick={onClick} className={`${base} ${skin}`} data-testid={testid}>{inner}</button>;
}

// ---- STAT TILE (inside the joined 1px-gap row) ---------------------------
function StatTile({ label, value, note, children, testid }) {
  return (
    <div className="bg-[#111213] p-4 flex flex-col" data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#76797D]">{label}</div>
      <div className="font-heading font-bold text-[27px] leading-none mt-2" style={{ color: CEIL }}>{value}</div>
      {note && <div className="text-[11px] text-[#76797D] mt-1.5">{note}</div>}
      {children}
    </div>
  );
}

function Card({ title, right, children, testid, className = "" }) {
  return (
    <section className={`border border-[#2E2F31] bg-[#111213] ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#2E2F31]">
        <h2 className="font-heading font-bold text-base uppercase tracking-tight" style={{ color: CEIL }}>{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

// Private WIP drafts (db.listing_drafts) — a seller's saved-but-unpublished listings. Renders only
// when the seller has at least one draft. Continue → /create?draft=<id> (hydrates the form).
function DraftsCard() {
  const [drafts, setDrafts] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/listings/drafts").then(({ data }) => setDrafts(data.drafts || [])).catch(() => setDrafts([]));
  }, []);
  const del = async (id) => {
    if (!window.confirm("Delete this draft? This can't be undone.")) return;
    try {
      await api.delete(`/listings/drafts/${id}`);
      toast.success("Draft deleted.");
      setDrafts((d) => (d || []).filter((x) => x.draft_id !== id));
    } catch { toast.error("Couldn't delete that draft."); }
  };
  if (!drafts || drafts.length === 0) return null;
  return (
    <Card title="Drafts" testid="card-drafts"
      right={<span className="font-mono text-[10px] uppercase tracking-wide text-[#E8C46A]">{drafts.length} saved</span>}>
      <div className="divide-y divide-[#2E2F31]" data-testid="my-drafts">
        {drafts.map((d) => (
          <div key={d.draft_id} className="flex items-center gap-3 p-3" data-testid={`draft-${d.draft_id}`}>
            <button onClick={() => navigate(`/create?draft=${d.draft_id}`)} className="w-12 h-12 shrink-0 border border-[#2E2F31] bg-obsidian overflow-hidden flex items-center justify-center">
              {d.thumbnail ? <img src={d.thumbnail} alt="" className="w-full h-full object-cover" /> : <FileText className="w-4 h-4 text-[#76797D]" />}
            </button>
            <div className="flex-1 min-w-0">
              <button onClick={() => navigate(`/create?draft=${d.draft_id}`)} className="font-body text-sm truncate block text-left hover:text-volt max-w-full" style={{ color: CEIL }}>{d.title || "Untitled draft"}</button>
              <div className="text-[11px] font-mono uppercase text-[#76797D] flex items-center gap-2 flex-wrap">
                <span className="text-[#E8C46A]">draft</span>
                {d.category && <span>· {d.category}</span>}
                <span>· edited {timeAgo(d.updated_at)}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => navigate(`/create?draft=${d.draft_id}`)} data-testid={`draft-continue-${d.draft_id}`} title="Continue editing" className="flex flex-col items-center gap-0.5 px-1.5 py-1 text-[#76797D] hover:text-[#DAD6CE] transition-colors">
                <Pencil className="w-4 h-4" /><span className="font-mono text-[8px] uppercase tracking-wide leading-none">Edit</span>
              </button>
              <button onClick={() => del(d.draft_id)} data-testid={`draft-delete-${d.draft_id}`} title="Delete draft" className="flex flex-col items-center gap-0.5 px-1.5 py-1 text-[#76797D] hover:text-blaze transition-colors">
                <Trash2 className="w-4 h-4" /><span className="font-mono text-[8px] uppercase tracking-wide leading-none">Delete</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// Invite & Vouch card — empty-state-first: lead with the link + the "why", not four zeros.
// No points number here (Phase 1); a one-line funnel summary appears only once there's activity.
function InviteCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/invites/me").then(({ data }) => setData(data)).catch(() => setData({ error: true })); }, []);
  const code = data?.code || "";
  const link = code ? `${window.location.origin}/i/${code}` : "";
  const stats = data?.stats || {};
  // Headline "invited" = VERIFIED members only. Pending signups are shown, not hidden, so the
  // number never looks broken to someone who just watched a friend sign up.
  const invited = stats.invited ?? stats.verified ?? 0;
  const pending = Math.max(0, (stats.joined || 0) - (stats.verified || 0));
  const released = stats.released || 0;
  const summary = [];
  if (invited) summary.push(`${invited} invited`);
  if (pending) summary.push(`${pending} pending verification`);
  if (released) summary.push(`${released} completed a deal`);
  const copy = async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success("Invite link copied"); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Couldn't copy — open Invites to grab it."); }
  };
  return (
    <Card title="Invite & Vouch" testid="card-invite" right={<UserPlus className="w-4 h-4 text-[#76797D]" />}>
      <div className="p-4">
        <p className="text-[12.5px] font-body text-[#8B8D90]">Invite someone you'd vouch for — your handle vouches for them until their first deal.</p>
        <div className="mt-3 flex items-stretch border border-[#2E2F31] bg-[#0D0E0F]">
          <span className="flex-1 min-w-0 px-3 py-2 font-mono text-[12px] text-[#DAD6CE] truncate" data-testid="dashboard-invite-link">{link || (data?.error ? "Unavailable" : "Loading…")}</span>
          <button onClick={copy} disabled={!link} data-testid="dashboard-invite-copy"
            className="shrink-0 inline-flex items-center gap-1.5 border-l border-[#2E2F31] bg-olive hover:bg-olive-bright disabled:opacity-40 text-white font-mono uppercase text-[10px] tracking-wide px-3 transition-colors">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          {summary.length > 0
            ? <span className="font-mono text-[10px] uppercase tracking-wide text-[#8FA84A]" data-testid="dashboard-invite-summary">{summary.join(" · ")}</span>
            : <span className="font-mono text-[10px] uppercase tracking-wide text-[#76797D]" data-testid="dashboard-invite-summary">No invited members yet</span>}
          <Link to="/invite" data-testid="dashboard-invite-link-btn"
            className="inline-flex items-center gap-1.5 border border-[#2E2F31] hover:border-[#3E3F42] text-[#DAD6CE] font-mono uppercase text-[10px] tracking-wide px-3 py-1.5 transition-colors">
            View invites <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <p className="mt-2 text-[10.5px] font-body text-[#76797D]" data-testid="dashboard-invite-count-note">
          "Invited" counts ID-verified members. Pending = signed up, not yet verified.
        </p>
      </div>
    </Card>
  );
}


// ONBOARDING SETUP CARD — the consolidated individual setup checklist. Shows what each step
// unlocks; only the current (first incomplete) step carries a CTA; the card disappears once the
// required steps are done. The payout step doubles as the payout callout.
function OnboardingCard({ s }) {
  const navigate = useNavigate();

  async function resendEmail() {
    try { await api.post("/auth/send-verification"); toast.success("Verification email sent — check your inbox."); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  const payoutRelevant = s.listings.total > 0 || s.payout?.connected || s.payout?.reconnect_required || s.is_dealer;
  const steps = [
    { id: "email", label: "Verify your email", unlocks: "Confirms your account so nothing important gets missed.",
      done: !!s.email_verified, cta: { label: "Resend email", run: resendEmail } },
    { id: "id", label: "Verify your ID", unlocks: "Required before your first listing goes live — about two minutes.",
      done: !!s.identity_verified, cta: { label: "Verify my ID", run: () => navigate("/verify"), testid: "dashboard-verify-btn" } },
    { id: "payout", label: "Set up payouts", reconnect: !!s.payout?.reconnect_required,
      unlocks: "Connect Stripe so sale funds reach you. Payments are held until the buyer confirms receipt.",
      done: !!s.payout?.connected, cta: { label: s.payout?.reconnect_required ? "Reconnect payouts" : "Set up payouts", run: () => navigate("/settings/payouts") } },
  ];

  const requiredDone = !!s.email_verified && !!s.identity_verified;
  const payoutDone = !!s.payout?.connected;
  const allDone = requiredDone && (!payoutRelevant || payoutDone);
  if (allDone) return null;

  const doneCount = steps.filter((st) => st.done).length;
  const currentId = steps.find((st) => !st.done && (st.id !== "payout" || payoutRelevant))?.id;

  return (
    <div className="bg-[#111213] border border-[#2E2F31] p-5" data-testid="dashboard-onboarding-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <LayoutDashboard className="w-5 h-5 shrink-0" style={{ color: "#748A3D" }} />
          <h3 className="font-heading font-bold text-[18px] uppercase tracking-tight" style={{ color: CEIL }}>Finish setting up</h3>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-wide text-[#8B8D90]" data-testid="onboarding-progress">
          {doneCount} of {steps.length} complete
        </span>
      </div>
      <div className="mt-4 space-y-2.5">
        {steps.map((st) => {
          const isCurrent = st.id === currentId;
          return (
            <div key={st.id} data-testid={`onboarding-step-${st.id}`}
              className={`flex items-start gap-3 border p-3 ${isCurrent ? "border-[#57682A] bg-[#141A0A]" : "border-[#2A2B2D] bg-[#0D0E0F]"}`}>
              <span className="mt-0.5 shrink-0">
                {st.done
                  ? <CheckCircle2 className="w-4 h-4" style={{ color: "#4e9e6f" }} />
                  : <span className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded-full border ${isCurrent ? "border-[#748A3D]" : "border-[#3E3F42]"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isCurrent ? "bg-[#748A3D]" : "bg-[#4a4c50]"}`} />
                    </span>}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold text-[14px] uppercase tracking-tight" style={{ color: st.done ? "#8B8D90" : CEIL }}>{st.label}</p>
                <p className="text-[12px] font-body text-[#8B8D90] mt-0.5">{st.unlocks}</p>
              </div>
              <div className="shrink-0">
                {st.done ? (
                  <Chip testid={`onboarding-chip-${st.id}`} label="Done" className="border" style={{ borderColor: "#1F7A4D", color: "#4e9e6f" }} />
                ) : st.reconnect ? (
                  <Chip testid={`onboarding-chip-${st.id}`} label="Reconnect" className="border" style={{ borderColor: "#A33D2880", color: "#E0A08F" }} />
                ) : isCurrent ? (
                  <button onClick={st.cta.run} data-testid={st.cta.testid || `onboarding-cta-${st.id}`}
                    className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-[12px] px-3.5 py-2 transition-colors whitespace-nowrap">
                    {st.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <Chip testid={`onboarding-chip-${st.id}`} label="Not started" className="border" style={{ borderColor: "#3E3F42", color: "#76797D" }} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


const ORDER_STATE = {
  held: "#E8C46A", shipped: "#5aa9e6", delivered: "#5aa9e6", released: "#4e9e6f",
  disputed: "#d9694f", refunded: "#8B8D90", cancelled: "#8B8D90", pending_payment: "#8B8D90",
  payout_held: "#E8C46A", release_pending: "#E8C46A", delivery_exception: "#d9694f",
};

// Seller fulfillment tracker — MY SALES only, with status tabs on top (mirrors the "Your Listings"
// simple-list layout). A buyer-side purchases view can be added later; this stays scoped to what
// the seller owes buyers.
const SALE_TABS = [
  { id: "all", label: "All" },
  { id: "needs_shipping", label: "Needs Shipping", statuses: ["held"] },
  { id: "shipped", label: "Shipped", statuses: ["shipped", "delivery_exception"] },
  { id: "complete", label: "Complete", statuses: ["released", "delivered", "release_pending", "payout_held"] },
  { id: "cancelled", label: "Cancelled", statuses: ["cancelled", "refunded"] },
];

function SalesOrdersCard({ sales }) {
  const [tab, setTab] = useState("all");
  const list = sales || [];
  const countFor = (t) => (t.id === "all" ? list.length : list.filter((o) => t.statuses.includes(o.status)).length);
  const active = SALE_TABS.find((t) => t.id === tab) || SALE_TABS[0];
  const rows = tab === "all" ? list : list.filter((o) => active.statuses.includes(o.status));
  return (
    <Card title="Orders" testid="card-sales-orders"
      right={<Link to="/orders" className="font-mono text-[10px] uppercase tracking-wide text-olive-bright hover:text-volt flex items-center gap-1">Manage all <ChevronRight className="w-3 h-3" /></Link>}>
      <div className="flex items-center gap-1 px-3 pt-3 pb-1 overflow-x-auto" data-testid="sales-order-tabs">
        {SALE_TABS.map((t) => {
          const n = countFor(t);
          const on = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`sales-order-tab-${t.id}`}
              className={`shrink-0 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide px-2.5 py-1.5 border transition-colors ${on ? "border-olive-bright bg-olive/10 text-[#DAD6CE]" : "border-[#2E2F31] text-[#76797D] hover:text-[#DAD6CE] hover:border-[#3E3F42]"}`}>
              {t.label}<span className={`${on ? "text-olive-bright" : "text-[#5c5e61]"}`}>{n}</span>
            </button>
          );
        })}
      </div>
      <div className="divide-y divide-[#2E2F31]" data-testid="sales-orders">
        {rows.length === 0 ? (
          <p className="p-5 text-sm text-[#76797D] font-body" data-testid="sales-orders-empty">
            {list.length === 0 ? "No sales yet — orders you need to fulfill will show here." : "Nothing in this status right now."}
          </p>
        ) : rows.map((o) => (
          <Link key={o.order_id} to="/orders" className="flex items-center gap-3 p-3 hover:bg-white/[0.02]" data-testid={`sales-order-${o.order_id}`}>
            <Package className="w-4 h-4 text-[#76797D] shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-body text-sm truncate block" style={{ color: CEIL }}>{o.listing_title || "Order"}</span>
              <div className="text-[11px] font-mono uppercase text-[#76797D] flex items-center gap-2 flex-wrap">
                <span>{timeAgo(o.created_at)}</span>
                {o.status === "shipped" && o.tracking_number && <span>· {o.carrier || "Tracking"} {o.tracking_number}</span>}
              </div>
            </div>
            <span className="font-mono text-[11px] uppercase shrink-0" data-testid={`sales-order-status-${o.order_id}`} style={{ color: ORDER_STATE[o.status] || "#8B8D90" }}>{(o.status || "").replace(/_/g, " ")}</span>
            <span className="font-mono text-sm shrink-0" style={{ color: CEIL }}>{money(o.amount_cents)}</span>
          </Link>
        ))}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user, setUser, refresh, businessFeaturesEnabled } = useAuth(); // eslint-disable-line
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [listings, setListings] = useState([]);
  const [orders, setOrders] = useState({ purchases: [], sales: [] });
  const [blocks, setBlocks] = useState([]);
  const [adminPending, setAdminPending] = useState(null);
  const [cancelRate, setCancelRate] = useState(null);
  const [inboxUnread, setInboxUnread] = useState(0);

  const load = () => {
    api.get("/me/dashboard-summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/me/listings").then(({ data }) => setListings(data.listings)).catch(() => {});
    api.get("/orders/mine").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/me/blocks").then(({ data }) => setBlocks(data.blocks)).catch(() => {});
    api.get("/orders/my-cancellation-rate").then(({ data }) => setCancelRate(data)).catch(() => {});
    // ONE unread source (same as the header badge + inbox): the consolidated inbox summary.
    api.get("/inbox/summary").then(({ data }) => setInboxUnread(data.total || 0)).catch(() => {});
  };
  useEffect(load, []);

  // CHECKOUT RETURN — verify the session server-side and apply the tier immediately, so a paying
  // member never lands on the wrong tier while waiting for the webhook. Idempotent on the backend.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("membership") !== "success") return;
    const sid = params.get("session_id");
    (async () => {
      try {
        if (sid) await api.post("/membership/confirm-checkout", { session_id: sid });
      } catch { /* webhook remains the authoritative fallback */ }
      try { await refresh(); } catch { /* ignore */ }
      load();
      toast.success("Membership active — your plan is now live.");
      params.delete("membership"); params.delete("session_id");
      navigate({ pathname: "/dashboard", search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (summary?.is_admin) api.get("/admin/pending-users/counts").then(({ data }) => setAdminPending(data)).catch(() => {});
  }, [summary?.is_admin]);

  const unblock = async (username) => {
    try { await api.delete(`/users/${username}/block`); toast.success(`@${username} unblocked.`); setBlocks((b) => b.filter((x) => x.blocked_username !== username)); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const bump = async (id) => {
    try {
      const { data } = await api.post(`/listings/${id}/bump`);
      const cd = data.cooldown_hours ? ` You can bump again in ${Math.round(data.cooldown_hours)}h.` : "";
      toast.success(data.charged > 0 ? `${money(Math.round(data.charged * 100))} applied. Moved to the top.${cd}` : `Moved to the top.${cd}`);
      load();
    } catch (err) {
      if (err.response?.status === 402) { toast.error(formatError(err.response?.data?.detail)); navigate("/credits"); return; }
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  if (!user || !summary) {
    return (
      <div className="min-h-screen text-foreground">
        <Navbar />
        <div className="max-w-[1160px] mx-auto px-4 py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-olive-bright" /></div>
      </div>
    );
  }

  const s = summary;
  const chip = primaryChip(s, user);
  const q = s.quota;
  const quotaSub = q.unlimited ? "Unlimited on this account" : `${q.remaining} of ${q.free_limit} free listing${q.free_limit !== 1 ? "s" : ""} left`;
  const recent = [
    ...(orders.sales || []).map((o) => ({ ...o, _side: "SOLD" })),
    ...(orders.purchases || []).map((o) => ({ ...o, _side: "BOUGHT" })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 5);

  // Third action tile is role-dependent (never a block the viewer can't act on).
  const thirdTile = s.is_admin
    ? <ActionTile to="/admin" label="Admin Portal" sub={`${adminPending?.total ?? 0} pending`} testid="tile-admin-portal" />
    : s.saved_search_eligible
    ? <ActionTile to="/feed" label="Saved Searches" sub="Alerts on new matches" testid="tile-saved-searches" />
    : <ActionTile dimmed label="Saved Searches" sub="Preferred and above" testid="tile-saved-searches-locked" />;

  // Stat tiles — FREE LISTINGS omitted entirely for unlimited accounts (a counter is meaningless).
  const statTiles = [];
  if (!q.unlimited) {
    statTiles.push(
      <StatTile key="quota" testid="stat-free-listings" label="Free Listings · 30d"
        value={`${q.used} / ${q.free_limit}`}
        note={q.next_is_paid ? `Next post ${money(Math.round(q.post_fee * 100))}` : `${q.remaining} left`} />
    );
  }
  statTiles.push(
    <StatTile key="listings" testid="stat-your-listings" label="Your Listings"
      value={s.listings.total} note={`${s.listings.active} active · ${s.listings.sold} sold`} />
  );
  if (cancelRate && (cancelRate.paid_orders > 0 || cancelRate.seller_cancellations > 0)) {
    statTiles.push(
      <StatTile key="cancelrate" testid="stat-cancellation-rate" label="Cancellation Rate"
        value={`${cancelRate.cancellation_rate_pct}%`}
        note={cancelRate.active_strikes > 0
          ? `${cancelRate.seller_cancellations} of ${cancelRate.paid_orders} · ${cancelRate.active_strikes} active strike${cancelRate.active_strikes !== 1 ? "s" : ""}`
          : `${cancelRate.seller_cancellations} of ${cancelRate.paid_orders} paid orders`} />
    );
  }
  statTiles.push(
    <StatTile key="fees" testid="stat-fees" label="Fees Paid · 30d"
      value={money(s.fees_30d.cents)} note={`${s.fees_30d.count} transaction${s.fees_30d.count !== 1 ? "s" : ""}`}>
      {s.savings && (
        <div className="-mx-4 -mb-4 mt-3 px-4 py-[11px] border-t" data-testid="stat-fees-savings"
          style={{ background: "rgba(207,232,107,0.07)", borderTopColor: "rgba(207,232,107,0.28)" }}>
          <div className="font-heading font-bold text-[21px] leading-none text-[#CFE86B] flex items-center gap-2 flex-wrap">
            <span>{money(s.savings.saved_cents)} more than</span>
            <span className="font-mono text-[11px] uppercase tracking-wide px-1.5 py-0.5" style={{ background: "#CFE86B", color: "#141A05" }}>{s.savings.next_tier_label}</span>
          </div>
          <div className="text-[10.5px] text-[#A9B884] mt-1.5">
            You'd have paid {money(s.savings.would_have_paid_cents)} ·{" "}
            <Link to="/pricing" data-testid="stat-fees-see-plans" className="font-mono uppercase tracking-wide text-[#CFE86B] hover:underline">See plans →</Link>
          </div>
        </div>
      )}
    </StatTile>
  );
  statTiles.push(
    <StatTile key="credit" testid="stat-store-credit" label="Store Credit"
      value={money(s.store_credit_cents)} note="Available balance" />
  );

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="max-w-[1160px] mx-auto px-4 py-7 space-y-5">

        <MemberWarningsBanner />

        {/* (1) HEADER */}
        <div className="flex items-end justify-between gap-3 flex-wrap" data-testid="dashboard-header">
          <h1 className="font-heading font-bold text-4xl sm:text-5xl uppercase tracking-tight" style={{ color: CEIL }}>Dashboard</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/u/${user.username}`} className="font-mono text-sm hover:underline" style={{ color: CEIL }} data-testid="dashboard-handle">@{user.username}</Link>
            <Chip testid="dashboard-tier-chip" label={chip.label} style={chip.style} />
            {s.comp_grant_type === "founding_6mo" && s.comped && s.comped_expires_at && (
              <Chip testid="dashboard-founding-comp-chip"
                label={`Founding Pro · comped until ${new Date(s.comped_expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`}
                className="border" style={{ borderColor: "#55451C", background: "#14110A", color: "#C8A64E" }} />
            )}
            {s.identity_verified
              ? <Chip testid="dashboard-id-chip" label="ID Verified" className="border" style={{ borderColor: "#1F7A4D", color: "#4e9e6f" }} />
              : <Chip testid="dashboard-id-chip" label="ID Unverified" className="border" style={{ borderColor: "#E8C46A80", color: "#E8C46A" }} />}
            {!s.email_verified && <Chip testid="dashboard-email-chip" label="Email Unverified" className="border" style={{ borderColor: "#E8C46A80", color: "#E8C46A" }} />}
          </div>
        </div>

        {/* (2) ONBOARDING SETUP CARD — consolidated setup checklist (email · ID · payouts).
            Replaces the ID-only verify strip; disappears once required setup is done. */}
        {!s.is_admin && <OnboardingCard s={s} />}

        {/* FOUNDING SEAT — persistent, dismissible claim prompt (queue-independent; always here
            while a seat is claimable). Live remaining count from the ledger. */}
        {!s.is_admin && <FoundingClaimPrompt mode="card" />}

        {/* BIRTHDAY REWARD STRIP — a pending free bump the member can spend now. */}
        {(user.rewards || []).some((r) => r.kind === "free_bump") && (
          <div className="bg-[#1a1608] border-l-[3px] px-[15px] py-[14px] flex items-center justify-between gap-4 flex-wrap"
            style={{ borderLeftColor: "#E8C46A" }} data-testid="dashboard-birthday-reward">
            <div className="flex items-start gap-3 min-w-0">
              <Cake className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#E8C46A" }} />
              <div className="min-w-0">
                <p className="font-heading font-bold text-[16px] uppercase" style={{ color: "#E8C46A" }}>
                  Birthday gift — free listing bump
                </p>
                <p className="text-[13px] font-body text-[#8B8D90] mt-0.5">
                  {(() => {
                    const b = (user.rewards || []).filter((r) => r.kind === "free_bump");
                    const exp = b[0]?.expires_at ? new Date(b[0].expires_at).toLocaleDateString() : null;
                    return `You have ${b.length} free bump${b.length > 1 ? "s" : ""}${exp ? ` — use by ${exp}` : ""}. Bump any active listing at no charge.`;
                  })()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* (3) ACTION TILES */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-[10px]" data-testid="dashboard-action-tiles">
          <ActionTile to="/create" filled label="+ Post a listing" sub={quotaSub} testid="tile-post-listing" />
          <ActionTile to="/feed" label="Browse marketplace" sub="Every live optic" testid="tile-browse" />
          {thirdTile}
          <ActionTile to="/inbox" label="Messages" sub={inboxUnread > 0 ? `${inboxUnread} unread` : "No unread"} testid="tile-messages" />
        </div>

        {/* (4) STAT TILES — joined row (1px gaps on a #2E2F31 field) */}
        <div className="bg-[#2E2F31] grid gap-[1px] items-stretch"
          style={{ gridTemplateColumns: `repeat(${statTiles.length}, minmax(0,1fr))` }}
          data-testid="dashboard-stat-tiles">
          {statTiles}
        </div>

        {/* (5) TWO COLUMNS */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_310px] gap-4 items-start">
          {/* LEFT */}
          <div className="space-y-4 min-w-0">
            <Card title="Your Listings" testid="card-your-listings"
              right={<Link to="/create" className="font-mono text-[10px] uppercase tracking-wide text-olive-bright hover:text-volt flex items-center gap-1">New <Plus className="w-3 h-3" /></Link>}>
              <div className="divide-y divide-[#2E2F31]" data-testid="my-listings">
                {listings.length === 0 && <p className="p-5 text-sm text-[#76797D] font-body">No listings yet. <Link to="/create" className="text-volt">Post your first optic.</Link></p>}
                {listings.slice(0, 8).map((l) => (
                  <div key={l.listing_id}
                    className={`flex items-center gap-3 p-3 ${l.status === "pending_review" ? "border-l-2 border-l-[#C2600F] bg-[#C2600F]/[0.06]" : ""}`}
                    data-testid={`manage-${l.listing_id}`}>
                    <Link to={listingPath(l)} className="w-12 h-12 shrink-0 border border-[#2E2F31] bg-obsidian overflow-hidden">
                      {(l.photos || [])[0] && <img src={l.photos[0]} alt="" className="w-full h-full object-cover" />}
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={listingPath(l)} className="font-body text-sm truncate block hover:text-volt" style={{ color: CEIL }}>{l.title}</Link>
                      <div className="text-[11px] font-mono uppercase text-[#76797D] flex items-center gap-2 flex-wrap">
                        <span className={l.status === "sold" ? "text-blaze" : l.status === "removed" ? "text-[#76797D]" : l.status === "draft" ? "text-[#E8C46A]" : "text-olive-bright"}>{l.status}</span>
                        <span>· {l.listing_type === "buy" ? "ISO" : `$${l.price}`}</span>
                        <span>· {timeAgo(l.created_at)}</span>
                        {l.status === "active" && l.has_variants && (() => {
                          const low = (l.variants || []).filter((v) => v.quantity >= 2 && (v.sold || 0) >= 1 && (v.quantity - (v.sold || 0)) >= 1 && (v.quantity - (v.sold || 0)) <= 2);
                          if (!low.length) return null;
                          const label = low.length === 1 && (low[0].quantity - (low[0].sold || 0)) === 1 ? "1 left" : `${low.length} option${low.length > 1 ? "s" : ""} low`;
                          return <span className="text-blaze border border-blaze/50 px-1 py-0.5 leading-none" data-testid={`lowstock-${l.listing_id}`}>{label}</span>;
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {l.status !== "sold" && (
                        <button onClick={() => navigate(`/edit/${l.listing_id}`)} title="Edit listing" className="flex flex-col items-center gap-0.5 px-1.5 py-1 text-[#76797D] hover:text-[#DAD6CE] transition-colors">
                          <Pencil className="w-4 h-4" /><span className="font-mono text-[8px] uppercase tracking-wide leading-none">Edit</span>
                        </button>
                      )}
                      {l.status === "active" && (
                        <button onClick={() => bump(l.listing_id)} data-testid={`bump-${l.listing_id}`} title="Bump Listing — resurface to the top of the feed" className="flex flex-col items-center gap-0.5 px-1.5 py-1 text-[#76797D] hover:text-olive-bright transition-colors">
                          <ArrowUpToLine className="w-4 h-4" /><span className="font-mono text-[8px] uppercase tracking-wide leading-none">Bump</span>
                        </button>
                      )}
                      {l.status !== "sold" && l.status !== "removed" && <CloseListingButton listingId={l.listing_id} onDone={load} variant="icon" listingTitle={l.title} />}
                    </div>
                  </div>
                ))}
                {listings.length > 8 && <p className="px-4 py-2 text-[11px] font-mono uppercase text-[#76797D]">+ {listings.length - 8} more</p>}
              </div>
            </Card>

            <DraftsCard />

            <SalesOrdersCard sales={orders.sales} />

            <Card title="Recent Orders" testid="card-recent-orders"
              right={recent.length > 0 && <Link to="/orders" className="font-mono text-[10px] uppercase tracking-wide text-olive-bright hover:text-volt flex items-center gap-1">All orders <ChevronRight className="w-3 h-3" /></Link>}>
              <div className="divide-y divide-[#2E2F31]" data-testid="recent-orders">
                {recent.length === 0 ? (
                  <p className="p-5 text-sm text-[#76797D] font-body">No orders yet — your buys and sales will show here.</p>
                ) : recent.map((o) => (
                  <Link key={o.order_id} to="/orders" className="flex items-center gap-3 p-3 hover:bg-white/[0.02]" data-testid={`order-${o.order_id}`}>
                    <Package className="w-4 h-4 text-[#76797D] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-body text-sm truncate block" style={{ color: CEIL }}>{o.listing_title || "Order"}</span>
                      <span className="text-[11px] font-mono uppercase text-[#76797D]">{o._side} · {timeAgo(o.created_at)}</span>
                    </div>
                    <span className="font-mono text-[11px] uppercase shrink-0" style={{ color: ORDER_STATE[o.status] || "#8B8D90" }}>{o.status?.replace("_", " ")}</span>
                    <span className="font-mono text-sm shrink-0" style={{ color: CEIL }}>{money(o.amount_cents)}</span>
                  </Link>
                ))}
              </div>
            </Card>
          </div>

          {/* RIGHT */}
          <div className="space-y-4">
            <Card title="Account" testid="card-account" right={
              <Link to="/profile" data-testid="dashboard-profile-badges-link"
                className="font-mono text-[10px] uppercase tracking-wide text-[#8FA84A] hover:text-volt transition-colors flex items-center gap-1">
                Profile &amp; Badges <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            }>
              <dl className="p-4 space-y-2.5 text-sm font-body">
                {[
                  [s.is_admin ? "Role" : "Plan", chip.label],
                  ["ID", s.identity_verified ? "Verified" : "Unverified"],
                  ["Email", s.email_verified ? "Verified" : "Unverified"],
                  ["Listing limit", q.unlimited ? "Unlimited" : `${q.free_limit} / mo free`],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <dt className="font-mono text-[10px] uppercase tracking-wide text-[#76797D]">{k}</dt>
                    <dd style={{ color: CEIL }}>{v}</dd>
                  </div>
                ))}
              </dl>
            </Card>

            <PayoutsRailCard />

            <InviteCard />

            {businessFeaturesEnabled && s.business_apply_eligible && (
              <Card title="Sell as a Business" testid="card-sell-business">
                <div className="p-4">
                  <p className="text-[12.5px] font-body text-[#8B8D90]">Running a shop or moving volume? Apply for Dealer / Business status — a Dealer badge, higher limits, and priority placement.</p>
                  {s.business_apply_pending ? (
                    <span data-testid="business-pending-chip" className="mt-3 inline-flex font-mono text-[11px] uppercase px-3 py-1.5 border border-[#E8C46A80] text-[#E8C46A]">Application pending review</span>
                  ) : (
                    <Link to="/dealer/apply" data-testid="business-apply-btn"
                      className="mt-3 inline-flex items-center gap-2 border border-[#2E2F31] hover:border-[#3E3F42] text-[#DAD6CE] font-mono uppercase text-[11px] tracking-wide px-4 py-2.5 transition-colors">
                      <Store className="w-3.5 h-3.5" /> Apply now
                    </Link>
                  )}
                </div>
              </Card>
            )}

            {businessFeaturesEnabled && (s.account_type === "business" || s.is_dealer || s.business_apply_pending) && (
              <Card title="Licenses & Compliance" testid="card-licenses"
                right={<FileCheck className="w-4 h-4 text-[#76797D]" />}>
                <div className="p-4">
                  <p className="text-[12.5px] font-body text-[#8B8D90]">
                    Verify your FFL to get the <span className="text-[#4a9fd6] font-semibold">Dealer Verified</span> badge and a pin on the map.
                  </p>
                  <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <span data-testid="dashboard-dealer-verified-chip"
                      className={`font-mono text-[10px] uppercase tracking-wide px-2 py-1 border ${(user?.ffl_verified || user?.dealer_verified) ? "border-[#1F6BA8] text-[#4a9fd6]" : "border-[#2E2F31] text-[#76797D]"}`}>
                      {(user?.ffl_verified || user?.dealer_verified) ? "Dealer verified" : "Not verified yet"}
                    </span>
                    <Link to="/dealer/licenses" data-testid="dashboard-licenses-btn"
                      className="inline-flex items-center gap-2 border border-[#2E2F31] hover:border-[#3E3F42] text-[#DAD6CE] font-mono uppercase text-[11px] tracking-wide px-4 py-2.5 transition-colors">
                      <FileCheck className="w-3.5 h-3.5" /> Manage licenses <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </Card>
            )}

            <Card title="Blocked" testid="card-blocked"
              right={<Ban className="w-4 h-4 text-[#76797D]" />}>
              <div className="divide-y divide-[#2E2F31]" data-testid="blocked-users-list">
                {blocks.length === 0 ? (
                  <p className="p-4 text-[12.5px] text-[#76797D] font-body">You haven't blocked anyone.</p>
                ) : blocks.map((b) => (
                  <div key={b.block_id} className="flex items-center gap-2 p-3" data-testid={`blocked-row-${b.blocked_username}`}>
                    <div className="flex-1 min-w-0">
                      <Link to={`/u/${b.blocked_username}`} className="font-body text-sm hover:text-volt" style={{ color: CEIL }}>@{b.blocked_username}</Link>
                      <div className="text-[10px] font-mono uppercase text-[#76797D]">{REASON_LABEL[b.reason] || b.reason} · {timeAgo(b.created_at)}</div>
                    </div>
                    <button onClick={() => unblock(b.blocked_username)} data-testid={`dashboard-unblock-${b.blocked_username}`}
                      className="shrink-0 inline-flex items-center gap-1 border border-blaze/50 text-blaze hover:bg-blaze/10 font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                      <ShieldOff className="w-3 h-3" /> Unblock
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
